import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { validateYupWithFeedback } from "services/forms/validation/feedback";
import { UserRow, UserRole } from "services/api/users/types";
import { getUserRolesAPI, getUsersAPI } from "services/api/users/api";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import MultiDropdown from "ui/components/Dropdown/MultiDropdown/MultiDropdown";
import { TableSearch } from "ui/components/Table/TableSearch";
import { TextField } from "ui/components/TextField/TextField";
import Badge from "ui/components/Badge/Badge";
import { ZPosition } from "ui/components/Modal/types";
import UserEditCard from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";
import { Pagination } from "services/search/types";
import { PaginationDirection } from "services/url";
import { NOTIFICATION_STEP3_SCHEMA } from "../validation";
import { StepProps } from "../types";
import { getRecipientColumns, initialRecipientsPagination } from "./consts";

const allUsersPagination: Pagination = {
  pageNumber: 1,
  pageSize: 500,
  sort: { sortBy: "firstName", direction: PaginationDirection.Ascending },
};

const Step3Recipients: React.FC<StepProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
  registerValidator,
}) => {
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [roleOptions, setRoleOptions] = useState<MultiDropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [pagination, setPagination] = useState<Pagination>(initialRecipientsPagination);

  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (registerValidator) {
      registerValidator(() => {
        const isYupValid = validateYupWithFeedback(formValues, NOTIFICATION_STEP3_SCHEMA, setErrors);
        if (!isYupValid) return false;

        const selectedUsers = allUsers.filter((u) =>
          formValues.includedUserIds.includes(u.id)
        );

        const missingEmail =
          formValues.channels & 1
            ? selectedUsers.filter((u) => !u.email?.trim())
            : [];

        const missingPhone =
          formValues.channels & 2
            ? selectedUsers.filter((u) => !u.mobilePhoneNumber?.trim())
            : [];

        if (missingEmail.length > 0 || missingPhone.length > 0) {
          const parts: string[] = [];
          if (missingEmail.length > 0) {
            const names = missingEmail.map((u) => `${u.firstName} ${u.lastName}`).join(", ");
            parts.push(`Nedostaje email: ${names}`);
          }
          if (missingPhone.length > 0) {
            const names = missingPhone.map((u) => `${u.firstName} ${u.lastName}`).join(", ");
            parts.push(`Nedostaje mobitel: ${names}`);
          }
          setErrors((prev) => ({ ...prev, includedUserIds: parts.join(". ") }));
          return false;
        }

        return true;
      });
    }
  }, [formValues, allUsers]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const result = await getUsersAPI(allUsersPagination);
      setAllUsers(result.data);
    } catch {
      // error handled by createApiCall
    }
    setIsLoading(false);
  };

  const fetchRoles = async () => {
    try {
      const roles = await getUserRolesAPI();
      setRoleOptions(
        roles.map((r: UserRole) => ({ value: r.id, text: r.name }))
      );
    } catch {
      // error handled by createApiCall
    }
  };

  // Client-side filtering
  const filteredUsers = useMemo(() => {
    let result = allUsers;

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter((u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term)
      );
    }

    if (roleFilter.length > 0) {
      const roleSet = new Set(roleFilter);
      result = result.filter((u) =>
        u.roles?.some((r) => roleSet.has(r.id))
      );
    }

    return result;
  }, [allUsers, search, roleFilter]);

  // Client-side pagination
  const paginatedData = useMemo(() => {
    const start = (pagination.pageNumber - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, pagination.pageNumber, pagination.pageSize]);

  const effectivePagination = useMemo(
    () => ({
      ...pagination,
      totalCount: filteredUsers.length,
      totalPages: Math.ceil(filteredUsers.length / pagination.pageSize),
    }),
    [filteredUsers.length, pagination]
  );

  // Selection logic (same pattern as Step2EntitySelection)
  const selectedSet = useMemo(
    () => new Set(formValues.includedUserIds),
    [formValues.includedUserIds]
  );

  const currentPageSelectedIds = useMemo(
    () => paginatedData.filter((u) => selectedSet.has(u.id)).map((u) => u.id),
    [paginatedData, selectedSet]
  );

  const handleSelectedChange = useCallback(
    (selected: number[] | number) => {
      if (Array.isArray(selected)) {
        if (selected.length === 0) {
          const pageIds = new Set(paginatedData.map((u) => u.id));
          setFormValues((prev) => ({
            ...prev,
            includedUserIds: prev.includedUserIds.filter(
              (id) => !pageIds.has(id)
            ),
          }));
        } else {
          const ids = selected as any as string[];
          setFormValues((prev) => {
            const next = new Set(prev.includedUserIds);
            ids.forEach((id) => next.add(id));
            return { ...prev, includedUserIds: Array.from(next) };
          });
        }
      } else {
        const id = selected as any as string;
        setFormValues((prev) => {
          const next = new Set(prev.includedUserIds);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return { ...prev, includedUserIds: Array.from(next) };
        });
      }
      setErrors(({ includedUserIds, ...rest }) => rest);
    },
    [paginatedData, setFormValues, setErrors]
  );

  const handleSearchChange = useCallback((e: any) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleRoleFilterChange = useCallback(
    (_name: string, items: MultiDropdownOption[]) => {
      setRoleFilter(items.map((i) => i.value));
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    },
    []
  );

  const handlePaginationChange = useCallback((newPagination: Pagination) => {
    setPagination(newPagination);
  }, []);

  const handleEditUser = useCallback((userId: string) => {
    setEditUserId(userId);
    setShowEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    setEditUserId(null);
    fetchUsers();
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
    setEditUserId(null);
  }, []);

  const columns = useMemo(
    () => getRecipientColumns(formValues.channels, handleEditUser),
    [formValues.channels, handleEditUser]
  );

  return (
    <>
      <div className="flex justify-center h-full px-4 mt-[100px]">
        <div className="w-full max-w-4xl">
          <div className="flex flex-col gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
                Odaberite primatelje
              </h1>
              <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
                Odaberite korisnike koji će primiti ovu notifikaciju
              </p>
            </div>

            <div className="border border-dashed bg-white border-blue-300 rounded-sm px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="flex gap-3">
                  <div className="w-64">
                    <TextField
                      type="text"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Pretraži po imenu..."
                      startIcon="/media/icons/search.svg"
                    />
                  </div>
                  <div className="w-64">
                    <MultiDropdown
                      name="roleFilter"
                      options={roleOptions}
                      value={roleFilter}
                      onChange={handleRoleFilterChange}
                      placeholder="Filtriraj po roli..."
                      width="full"
                      height="small"
                    />
                  </div>
                </div>
                <Badge
                  text={`${formValues.includedUserIds.length} odabrano`}
                  variant="info"
                  height="small"
                />
              </div>

              <TableSearch
                data={paginatedData}
                columns={columns}
                selectableRows={true}
                showSelectAll={true}
                selectedRows={currentPageSelectedIds as any}
                onSelectedChange={handleSelectedChange}
                pagination={effectivePagination}
                onPaginationChange={handlePaginationChange}
                isLoading={isLoading}
              />

              {errors.includedUserIds && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.includedUserIds}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        Otvara se nad modalom obavijesti, pa mu treba viši sloj: modal s uvodnom plohom
        Modal prisilno stavlja na dno, a obavijest bi ga pokrila.
      */}
      <UserEditCard
        userId={editUserId}
        show={showEditModal}
        isFullScreen={false}
        zPosition={ZPosition.Top}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />
    </>
  );
};

export default memo(Step3Recipients);
